import { calculateRideMetrics, type RideMetrics, type RidePoint, type RideProgress } from "./ride";

type Position = Pick<RideProgress, "currentPoint" | "pointIndex">;

/** Compiles cumulative telemetry once, then evaluates only the current partial segment. */
export function compileRideMetrics(points: readonly RidePoint[]): (position: Position) => RideMetrics {
  // Use the shared calculator for validation and each segment's metric semantics.
  calculateRideMetrics(points);
  const first = points[0]!;
  const startTime = Date.parse(first.recordedAt);
  const elapsed = (point: RidePoint) => (Date.parse(point.recordedAt) - startTime) / 1_000;
  const totals: RideMetrics[] = [calculateRideMetrics([first, first])];
  for (let index = 1; index < points.length; index += 1) {
    totals.push(combine(totals[index - 1]!, calculateRideMetrics([points[index - 1]!, points[index]!]), elapsed(points[index]!)));
  }
  return ({ currentPoint, pointIndex }) => combine(
    totals[pointIndex]!,
    calculateRideMetrics([points[pointIndex]!, currentPoint]),
    elapsed(currentPoint),
  );
}

/** Joins adjacent metric spans, deriving average speed from their combined time. */
function combine(prefix: RideMetrics, segment: RideMetrics, durationSeconds: number): RideMetrics {
  const distanceKilometers = prefix.distanceKilometers + segment.distanceKilometers;
  return {
    distanceKilometers, durationSeconds,
    ascentMeters: prefix.ascentMeters + segment.ascentMeters,
    descentMeters: prefix.descentMeters + segment.descentMeters,
    minimumElevationMeters: Math.min(prefix.minimumElevationMeters, segment.minimumElevationMeters),
    maximumElevationMeters: Math.max(prefix.maximumElevationMeters, segment.maximumElevationMeters),
    averageKilometersPerHour: durationSeconds === 0 ? 0 : distanceKilometers / (durationSeconds / 3_600),
  };
}
